// SubmitComplaint.jsx
import React, { useState, useMemo, useContext, useCallback } from "react";
import { Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import { submitComplaintApi } from "../../http/bookAPI";
import { Context } from "../../index";

/**
 * Props:
 * - assignmentId: number | string (обязателен)
 * - staffMessage?: string
 * - defaultReason?: string
 * - onSubmitted?: (payload|void) => void
 * - end_rev_date?: number (в минутах с эпохи; конечная точка процесса подтверждения отзыва)  👈 ДОБАВЛЕНО
 */
const SubmitComplaint = ({
                             assignmentId,
                             staffMessage = "",
                             defaultReason = "",
                             onSubmitted,
                             end_rev_date = 0, // 👈 минуты (Unix minutes). Если 0 — считаем, что даты нет (разрешим форму по-умолчанию).
                         }) => {
    const templates = useMemo(
        () => [
            {
                key: "factually_incorrect",
                label: "The review contains factual errors.",
                text:
                    "The review contains factual inaccuracies about the book’s content (plot, characters, claims). These errors may mislead readers. Please review and take appropriate action."
            },
            {
                key: "violates_guidelines",
                label: "The review violates Amazon guidelines (personal attacks/offensive).",
                text:
                    "The review includes personal attacks/offensive language, which appears to violate Amazon’s community guidelines. Kindly evaluate and remove content that doesn’t comply."
            },
            {
                key: "wrong_product_asin",
                label: "The review seems to be for a different product/ASIN.",
                text:
                    "Details in the review do not match this book’s title/edition/ASIN. It appears to be attached to the wrong product page. Please verify and reassign or remove the review."
            },
            {
                key: "irrelevant_content",
                label: "The review is irrelevant (not about the book itself).",
                text:
                    "The review focuses on issues unrelated to the book (e.g., shipping, pricing, external factors) rather than its content or quality. Please consider removing or relocating this feedback."
            },
            {
                key: "low_quality_ai_like",
                label: "Low-quality or AI-like review (no specific references).",
                text:
                    "The review is overly generic and lacks book-specific details, suggesting very low quality or automated/AI-like text. Please assess authenticity and compliance."
            },
            {
                key: "incentivized_conflict",
                label: "Possibly incentivized or conflict of interest.",
                text:
                    "There are signs the review may be incentivized or affected by a conflict of interest, which can mislead readers. Please check compliance with fair-review policies."
            },
            {
                key: "review_disappeared_refund",
                label: "The review has disappeared from Amazon (request refund).",
                text:
                    "The review is no longer visible or reachable on Amazon. Since it has disappeared, I kindly request a refund in accordance with your policy."
            }
        ],
        []
    );


    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [reason, setReason] = useState(defaultReason);
    const [submitting, setSubmitting] = useState(false);
    const [serverMsg, setServerMsg] = useState(null);
    const { user } = useContext(Context);
    const allowedMonthsByPlan = {
        FREE: 1,
        ESSENTIAL: 2,
        PREMIUM: 3,
        MAXIMUM: 6,
    };

    const tariffPlan = user?.user?.tariff_plan || "FREE";
    const baseMonths = allowedMonthsByPlan[tariffPlan] ?? 1;
    const allowedMonths = Math.min(baseMonths, 6);

    // end_rev_date приходит в МИНУТАХ → переводим в миллисекунды
    const confirmationMs = end_rev_date ? end_rev_date * 60 * 1000 : 0;
    const confirmationDate = confirmationMs ? new Date(confirmationMs) : null;

    // Добавляем allowedMonths к дате подтверждения, чтобы получить deadline
    const deadlineMs = useMemo(() => {
        if (!confirmationMs) return 0;
        const d = new Date(confirmationMs);
        d.setMonth(d.getMonth() + allowedMonths);
        return d.getTime();
    }, [confirmationMs, allowedMonths]);

    const now = Date.now();
    const isWindowKnown = Boolean(confirmationMs);
    const isExpired = isWindowKnown ? now > deadlineMs : false;

    const formattedDate = confirmationDate
        ? confirmationDate.toLocaleDateString("en-US", {
            day: "numeric",
            month: "long",
            year: "numeric",
        })
        : "Unknown";

    // Человеческие подсказки (оставшихся дней и т.п.)
    const remainingText = useMemo(() => {
        if (!isWindowKnown) return "";
        const msLeft = deadlineMs - now;
        if (msLeft <= 0) return "Complaint window has expired.";
        const days = Math.ceil(msLeft / (1000 * 60 * 60 * 24));
        return `You have approximately ${days} day(s) remaining to submit a complaint.`;
    }, [isWindowKnown, deadlineMs, now]);

    const applyTemplate = (key) => {
        setSelectedTemplate(key);
        const t = templates.find((t) => t.key === key);
        if (t) setReason(t.text);
    };

    const canSubmit = assignmentId && reason?.trim().length >= 10 && !submitting;

    const handleSubmit = useCallback(async () => {
        setServerMsg(null);
        if (!assignmentId) {
            setServerMsg({
                type: "danger",
                text: "Assignment ID is missing. Please refresh the page.",
            });
            return;
        }
        if (!reason || reason.trim().length < 10) {
            setServerMsg({
                type: "warning",
                text: "Please provide a short description (at least 10 characters).",
            });
            return;
        }

        setSubmitting(true);
        try {
            const res = await submitComplaintApi({
                assignmentId,
                status: "COMPLAINT",
                message: reason.trim(),
            });
            const text = res?.message || "Complaint has been submitted successfully.";
            setServerMsg({ type: "success", text });
            if (typeof onSubmitted === "function") onSubmitted(res);
        } catch (e) {
            const text =
                e?.response?.data?.message ||
                e?.message ||
                "Failed to submit the complaint. Please try again.";
            setServerMsg({ type: "danger", text });
        } finally {
            setSubmitting(false);
        }
    }, [assignmentId, reason, onSubmitted]);

    // ====== Текстовое уведомление под полем ======
    // Перевод и вежливое объяснение окна приёма жалоб:
    const policyNote = (
        <div className=" text-muted mt-2">
            Dear authors and readers, as you know, Amazon can occasionally behave
            unpredictably. Because of this, we cannot keep complaint submissions open
            indefinitely. The complaint window is limited as follows:{" "}
            <strong>Free</strong> — within 1 month of the review confirmation date;{" "}
            <strong>Essential</strong> — 2 months; <strong>Premium</strong> — 3 months; and{" "}
            <strong>Maximum users - 6 months</strong> overall. Beyond that, tracking loses its
            meaning. Please keep this in mind.
        </div>
    );

    // ====== Если окно истекло — показываем заглушку-карточку ======
    if (isWindowKnown && isExpired) {
        return (
            <Card className="border-secondary">
                <Card.Header className="py-1">Complaint Submission for this Assignment is Closed</Card.Header>
                <Card.Body className="p-3">
                    <Alert variant="secondary" className=" py-2">
                        The complaint window for this assignment has expired. Review confirmation date:{" "}
                        <strong>{formattedDate}</strong>. Allowed window by your plan:{" "}
                        <strong>{allowedMonths} month(s)</strong>.
                    </Alert>

                    {policyNote}

                    <div className="d-flex justify-content-end mt-3">
                        <Button size="sm" variant="secondary" disabled>
                            Closed
                        </Button>
                    </div>
                </Card.Body>
            </Card>
        );
    }

    // ====== Основной сценарий — форма доступна ======
    return (
        <Card className="border-secondary">
            <Card.Header className="py-1">
                Submit Complaint. Found something wrong with this review? Let us know.
            </Card.Header>
            <Card.Body className="p-3">
                {/* Подсказка по срокам, если дата известна */}
                {isWindowKnown && (
                    <Alert variant="light" className="py-2 border border-secondary-subtle">
                        <div>
                            <strong>Complaint Window:</strong>  Review confirmation date: <strong>{formattedDate}</strong>. Allowed window by your plan:{" "}
                            <strong>{allowedMonths} month(s)</strong>.{" "}
                            {remainingText}
                        </div>
                    </Alert>
                )}

                {/* Template selector */}
                <Form.Group className="mb-2">
                    <Form.Label className=" text-muted mb-0">
                        Pick a template (optional)
                    </Form.Label>
                    <Form.Select
                        size="sm"
                        value={selectedTemplate}
                        onChange={(e) => applyTemplate(e.target.value)}
                        disabled={submitting}
                        className="border border-secondary"
                    >
                        <option value="">— Choose a template —</option>
                        {templates.map((t) => (
                            <option key={t.key} value={t.key}>
                                {t.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>

                {/* Free text (always visible) */}
                <Form.Group className="mb-2">
                    <Form.Label className="text-muted mb-0">
                        Describe your complaint
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Write your reason here (you can edit the template text)…"
                        disabled={submitting}
                        className="form-control-sm small text-muted border border-secondary"
                    />
                    <div className="small text-muted mt-1">
                        Please ensure your message is clear and factual. Minimum 10
                        characters.
                    </div>

                    {/* Добавленный блок-пояснение по правилам и срокам */}
                    {policyNote}
                </Form.Group>

                {/* Staff message (if any) */}
                {staffMessage?.trim() ? (
                    <Alert variant="info" className="py-2 small">
                        <div className="fw-semibold mb-1">Staff Message</div>
                        <div style={{ whiteSpace: "pre-wrap" }}>{staffMessage}</div>
                    </Alert>
                ) : null}

                {/* Server response */}
                {serverMsg?.text ? (
                    <Alert variant={serverMsg.type || "info"} className="py-2 small">
                        {serverMsg.text}
                    </Alert>
                ) : null}

                {/* Submit button */}
                <div className="d-flex justify-content-end">
                    <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                    >
                        {submitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Submitting…
                            </>
                        ) : (
                            "Submit Complaint"
                        )}
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

export default SubmitComplaint;
